<template>
	<div id="hero-top" class="landing-page-root">
		<!-- ========================================================================= -->
		<!-- 1. FULL-WIDTH MODERN HERO BANNER WITH MOUSE-MOVE 3D INTERACTION          -->
		<!-- ========================================================================= -->
		<section ref="heroBanner" class="hero-banner-section" @mousemove="onHeroMouseMove" @mouseleave="onHeroMouseLeave">
			<!-- Ambient lighting and glowing mesh elements -->
			<div class="hero-spotlight-glow" :style="{
				background: `radial-gradient(circle 550px at ${spotlightX}% ${spotlightY}%, rgba(82, 46, 168, 0.15), rgba(110, 65, 220, 0.05) 50%, transparent 80%)`
			}"></div>
			<div class="hero-orb hero-orb-1"></div>
			<div class="hero-orb hero-orb-2"></div>
			<div class="hero-grid-pattern"></div>

			<!-- Centered content wrapper -->
			<v-container class="hero-container py-10 py-md-16">
				<v-row class="align-center fill-height">
					<!-- RIGHT COLUMN (in RTL): 3D Mockup Visual & Parallax Badges -->
					<v-col cols="12" md="6" class="order-1 order-md-1">
						<div class="hero-visual-wrapper">
							<!-- 3D Perspective Card that tilts with mouse move -->
							<div class="hero-3d-card" :style="{
								transform: `perspective(1100px) rotateX(${tiltX}deg) rotateY(${tiltY}deg) scale3d(1.02, 1.02, 1.02)`
							}">
								<!-- Top Window Mockup Bar -->
								<div class="mockup-window-header">
									<div class="mockup-dots">
										<span class="dot dot-red"></span>
										<span class="dot dot-yellow"></span>
										<span class="dot dot-green"></span>
									</div>
									<div class="mockup-title">
										<v-icon small color="#522ea8" class="ml-1">mdi-shield-check</v-icon>
										<span>سامانه ابری جت اقساط | پنل مدیریت</span>
									</div>
								</div>

								<!-- Main software preview image -->
								<div class="mockup-screen-body">
									<img :src="require('@/assets/banner-jet-aghsat.jpg')" alt="داشبورد نرم افزار جت اقساط" class="mockup-main-image" />
									<div class="mockup-glass-sheen" :style="{
										background: `linear-gradient(${120 + tiltY * 3}deg, rgba(255, 255, 255, 0.35) 0%, transparent 60%)`
									}"></div>
								</div>
							</div>

							<!-- Floating Parallax Badge 1 (Top Left) -->
							<div class="parallax-badge badge-top" :style="{
								transform: `translate3d(${badgeX1}px, ${badgeY1}px, 40px)`
							}">
								<div class="badge-icon-box bg-amber">
									<v-icon color="#e65100" small>mdi-flash</v-icon>
								</div>
								<div class="badge-text-box">
									<span class="badge-primary-text">شروع سریع</span>
									<span class="badge-secondary-text">راه‌اندازی در کمتر از ۲ دقیقه</span>
								</div>
							</div>

							<!-- Floating Parallax Badge 2 (Bottom Right) -->
							<div class="parallax-badge badge-bottom-right" :style="{
								transform: `translate3d(${badgeX2}px, ${badgeY2}px, 50px)`
							}">
								<div class="badge-icon-box bg-purple">
									<v-icon color="#522ea8" small>mdi-calculator-variant-outline</v-icon>
								</div>
								<div class="badge-text-box">
									<span class="badge-primary-text">محاسبه خودکار</span>
									<span class="badge-secondary-text">سود، دیرکرد و تسویه آنی</span>
								</div>
							</div>

							<!-- Floating Parallax Badge 3 (Bottom Left) -->
							<div class="parallax-badge badge-bottom-left" :style="{
								transform: `translate3d(${badgeX3}px, ${badgeY3}px, 45px)`
							}">
								<div class="badge-icon-box bg-emerald">
									<v-icon color="#2e7d32" small>mdi-message-check-outline</v-icon>
								</div>
								<div class="badge-text-box">
									<span class="badge-primary-text">پیامک هوشمند</span>
									<span class="badge-secondary-text">یادآوری خودکار با لینک پرداخت</span>
								</div>
							</div>
						</div>
					</v-col>

					<!-- LEFT COLUMN (in RTL): Titles, Subtitle & 2 CTA Buttons -->
					<v-col cols="12" md="6" class="order-2 order-md-2 pr-md-8">
						<div class="hero-text-content">
							<!-- Announcement Chip -->
							<router-link to="/landing" class="text-decoration-none">
								<div class="hero-chip mb-4" style="cursor: pointer;">
									<span class="hero-pulse-dot"></span>
									<v-icon small color="#ff9800" class="mx-1">mdi-lightning-bolt</v-icon>
									<span class="font-weight-bold">فروش قسطی بدون دنبال مشتری دویدن؛ با جت اقساط</span>
									<v-icon x-small color="#ff9800" class="mr-1">mdi-arrow-left</v-icon>
								</div>
							</router-link>

							<!-- Main Hero Title -->
							<h1 class="hero-title mb-4">
								نرم‌افزار تحت وب و ابری
								<span class="hero-title-highlight">مدیریت وام و اقساط</span>
								ویژه اصناف
							</h1>

							<!-- Hero Subtitle -->
							<p class="hero-subtitle mb-8">
								سامانه‌ای آنلاین، هوشمند و ابری ویژه کلیه اصناف، فروشگاه‌ها، صندوق‌های قرض‌الحسنه و وام‌های خانوادگی؛ با قابلیت محاسبه خودکار سود و دیرکرد، ارسال خودکار پیامک یادآوری، درگاه پرداخت آنلاین بانکی و تسویه لحظه‌ای بدون نیاز به نصب هرگونه برنامه.
							</p>

							<!-- Two Primary CTA Buttons -->
							<div class="hero-actions mb-8">
								<v-btn large elevation="4" rounded class="hero-btn-primary ml-sm-4 mb-3" @click="scrollTo('test-register')">
									<v-icon left>mdi-rocket-launch-outline</v-icon>
									<span>قبل از خرید تست کنید</span>
								</v-btn>

								<v-btn large outlined rounded class="hero-btn-secondary ml-sm-4 mb-3" @click="scrollTo('consultation')">
									<v-icon left>mdi-phone-in-talk-outline</v-icon>
									<span>درخواست مشاوره</span>
								</v-btn>

								<v-btn large elevation="3" rounded class="hero-btn-whatsapp" :href="whatsappUrl" target="_blank">
									<v-icon left size="22">mdi-whatsapp</v-icon>
									<span>پشتیبانی واتساپ</span>
								</v-btn>
							</div>

							<!-- Key Value Badges Row -->
							<div class="hero-trust-row">
								<div class="trust-item">
									<v-icon small color="#522ea8">mdi-check-decagram</v-icon>
									<span>۱۰ روز مهلت تست رایگان</span>
								</div>
								<div class="trust-item">
									<v-icon small color="#522ea8">mdi-cloud-outline</v-icon>
									<span>۱۰۰٪ ابری و تحت وب</span>
								</div>
								<div class="trust-item">
									<v-icon small color="#522ea8">mdi-credit-card-fast-outline</v-icon>
									<span>اتصال به درگاه آنلاین بانکی</span>
								</div>
							</div>
						</div>
					</v-col>
				</v-row>
			</v-container>
		</section>

		<!-- ========================================================================= -->
		<!-- 2. NEW SECTION: "جت اقساط در ۲ دقیقه" WITH APARAT VIDEO EMBED              -->
		<!-- ========================================================================= -->
		<section id="jet-2min" class="landing-section jet-2min-section">
			<v-container style="max-width: 1240px;">
				<div class="section-badge-header text-center mb-10">
					<div class="section-chip mb-2">
						<v-icon small color="#e65100">mdi-play-circle-outline</v-icon>
						<span>ویدیو معرفی و راهنما</span>
					</div>
					<h2 class="section-main-heading">
						جت اقساط در ۲ دقیقه
					</h2>
					<p class="section-sub-heading">
						با مشاهده ویدیوی زیر، در ۲ دقیقه نحوه سر و سامان دادن به اقساط مشتریان آشنا بشید.
					</p>
				</div>

				<v-row class="align-center">
					<!-- Video Player Column (Aparat Embed) -->
					<v-col cols="12" lg="7">
						<div class="video-card-container">
							<div class="video-card-inner">
								<div class="video-frame-wrapper">
									<iframe src="https://www.aparat.com/video/video/embed/videohash/njm27g7/vt/frame" allowFullScreen="true" webkitallowfullscreen="true" mozallowfullscreen="true" title="ویدیو معرفی جت اقساط در ۲ دقیقه"></iframe>
								</div>
								<div class="video-footer-strip">
									<div class="d-flex align-center">
										<div class="video-pulse-play ml-2">
											<v-icon small color="#ffffff">mdi-play</v-icon>
										</div>
										<span class="video-title-text">آموزش گام‌به‌گام و معرفی قابلیت‌های جت اقساط</span>
									</div>
									<a href="https://aparat.com/v/njm27g7" target="_blank" class="aparat-link-btn">
										<span>مشاهده در آپارات</span>
										<v-icon x-small class="mr-1">mdi-open-in-new</v-icon>
									</a>
								</div>
							</div>
						</div>
					</v-col>

					<!-- Complementary Info Points Column -->
					<v-col cols="12" lg="5" class="pr-lg-8 mt-6 mt-lg-0">
						<div id="features" class="jet-benefits-box">
							<h3 class="jet-benefits-title mb-4">
								چرا مدیریت اقساط با «جت اقساط» آسان است؟
							</h3>

							<!-- 1. داشبورد تحلیلی و گزارش‌های مالی (جایگزین ثبت‌نام و راه‌اندازی فوری) -->
							<div class="benefit-card mb-3">
								<div class="benefit-icon bg-light-purple">
									<v-icon color="#522ea8">mdi-poll</v-icon>
								</div>
								<div class="benefit-content flex-grow-1">
									<div class="d-flex align-center justify-space-between flex-wrap gap-1 mb-1">
										<h4>داشبورد تحلیلی و گزارش‌های مالی</h4>
										<v-btn small rounded outlined color="#522ea8" to="/financial-reports" class="font-weight-bold benefit-action-btn">
											<span>اطلاعات بیشتر</span>
											<v-icon right x-small>mdi-arrow-left</v-icon>
										</v-btn>
									</div>
									<p>ارائه آمار دقیق جامع وام‌ها، اقساط وصولی و معوق، ثبت و مدیریت واریز و برداشت بانک و صندوق و خروجی اکسل.</p>
								</div>
							</div>

							<!-- 2. تعیین بهره و فرمول‌های پیشرفته وام (جایگزین فرمول‌های منعطف محاسبه وام) -->
							<div class="benefit-card mb-3">
								<div class="benefit-icon bg-light-amber">
									<v-icon color="#e65100">mdi-calculator</v-icon>
								</div>
								<div class="benefit-content flex-grow-1">
									<div class="d-flex align-center justify-space-between flex-wrap gap-1 mb-1">
										<h4>تعیین بهره و فرمول‌های پیشرفته وام</h4>
										<v-btn small rounded outlined color="#e65100" to="/loan-formulas" class="font-weight-bold benefit-action-btn">
											<span>اطلاعات بیشتر</span>
											<v-icon right x-small>mdi-arrow-left</v-icon>
										</v-btn>
									</div>
									<p>تعریف نامحدود الگوهای وام با کارمزد دلخواه، جریمه دیرکرد، پاداش پرداخت زودهنگام و محاسبه تسویه آنی لحظه‌ای.</p>
								</div>
							</div>

							<!-- 3. دستیار خودکار پیامک و یادآوری (سر جای خود باقی می‌ماند) -->
							<div class="benefit-card mb-3">
								<div class="benefit-icon bg-light-purple">
									<v-icon color="#522ea8">mdi-cellphone-message</v-icon>
								</div>
								<div class="benefit-content flex-grow-1">
									<div class="d-flex align-center justify-space-between flex-wrap gap-1 mb-1">
										<h4>دستیار خودکار پیامک و یادآوری</h4>
										<v-btn small rounded outlined color="#522ea8" to="/smart-sms" class="font-weight-bold benefit-action-btn">
											<span>اطلاعات بیشتر</span>
											<v-icon right x-small>mdi-arrow-left</v-icon>
										</v-btn>
									</div>
									<p>ارسال خودکار پیامک قبل از موعد، روز سررسید، تشکر از واریز و اخطار تاخیر به همراه لینک اختصاصی پرداخت آنلاین.</p>
								</div>
							</div>

							<!-- 4. درگاه پرداخت مستقیم برای مشتریان (سر جای خود باقی می‌ماند) -->
							<div class="benefit-card mb-3">
								<div class="benefit-icon bg-light-emerald">
									<v-icon color="#2e7d32">mdi-credit-card-check-outline</v-icon>
								</div>
								<div class="benefit-content flex-grow-1">
									<div class="d-flex align-center justify-space-between flex-wrap gap-1 mb-1">
										<h4>درگاه پرداخت مستقیم برای مشتریان</h4>
										<v-btn small rounded outlined color="#2e7d32" to="/online-payment" class="font-weight-bold benefit-action-btn">
											<span>اطلاعات بیشتر</span>
											<v-icon right x-small>mdi-arrow-left</v-icon>
										</v-btn>
									</div>
									<p>مشتریان بدون نیاز به کارت‌به‌کارت و ارسال فیش، اقساط خود را ۲۴ ساعته پرداخت و رسید دیجیتال دریافت می‌کنند.</p>
								</div>
							</div>

							<!-- 5. پنل کاربری مشتریان و صندوق‌های وام (کارت جدید) -->
							<div class="benefit-card mb-5">
								<div class="benefit-icon bg-light-blue">
									<v-icon color="#0288d1">mdi-account-group-outline</v-icon>
								</div>
								<div class="benefit-content flex-grow-1">
									<div class="d-flex align-center justify-space-between flex-wrap gap-1 mb-1">
										<h4>پنل کاربری مشتریان و صندوق‌های وام</h4>
										<v-btn small rounded outlined color="#0288d1" to="/customer-portal" class="font-weight-bold benefit-action-btn">
											<span>اطلاعات بیشتر</span>
											<v-icon right x-small>mdi-arrow-left</v-icon>
										</v-btn>
									</div>
									<p>مشاهده ریز پرداخت‌ها، اقساط مانده، سوابق مالی، پرداخت آنلاین اقساط توسط مشتریان و ثبت پس‌انداز ماهانه اعضا.</p>
								</div>
							</div>

							<div class="d-flex flex-wrap gap-3">
								<v-btn color="#522ea8" dark rounded large elevation="2" class="px-6 ml-sm-4 font-weight-bold" @click="scrollTo('test-register')">
									<v-icon left>mdi-play</v-icon>
									همین الان تست کنید
								</v-btn>

								<v-btn outlined color="#522ea8" rounded large class="px-6 font-weight-bold" @click="scrollTo('pricing')">
									مشاهده قیمت‌ها
								</v-btn>
							</div>
						</div>
					</v-col>
				</v-row>
			</v-container>
		</section>

		<!-- ========================================================================= -->
		<!-- 3. CREDIT INQUIRY & VALIDATION SHOWCASE SECTION (معرفی اعتبارسنجی آنلاین)   -->
		<!-- ========================================================================= -->
		<section id="inquiry-showcase" class="landing-section bg-soft-lilac inquiry-feature-section">
			<v-container style="max-width: 1240px;">
				<div class="inquiry-showcase-card pa-8 pa-md-12 rounded-2xl">
					<v-row class="align-center">
						<!-- Right Column (in RTL): Visual Inquiry Image -->
						<v-col cols="12" md="5" class="order-2 order-md-1">
							<div class="inquiry-mockup-wrapper text-center">
								<img src="/inquiry.png" alt="استعلام و اعتبارسنجی آنلاین مشتریان" class="inquiry-feature-img rounded-2xl elevation-4" />
							</div>
						</v-col>

						<!-- Left Column (in RTL): Content & CTA Link to CreditInquiryLanding -->
						<v-col cols="12" md="7" class="order-1 order-md-2 pr-md-8">
							<div class="section-chip mb-3">
								<v-icon small color="#2e7d32">mdi-shield-search</v-icon>
								<span>اتصال به سرویس‌های استعلام گیری</span>
							</div>

							<h2 class="section-main-heading mb-4">
								اعتبارسنجی و استعلام آنلاین مشتریان
								<br />
								<span class="hero-title-highlight">اول بررسی کن، بعد تصمیم بگیر!</span>
							</h2>

							<p class="text-body-1 line-height-2d2 grey--text text--darken-2 mb-6 text-justify">
								هر مشتری، یک ریسک متفاوت دارد. ممکن است دو مشتری هر دو درخواست خرید قسطی داشته باشند، اما توانایی پرداخت و سابقه مالیشان یکسان نباشد. با سرویس اعتبارسنجی جت اقساط، تصمیم برای فروش اقساطی را بر اساس اطلاعات و سوابق موثق بگیرید، نه فقط بر اساس حدس و گمان!
							</p>

							<div class="d-flex flex-wrap gap-3 align-center">
								<v-btn large rounded elevation="3" color="#522ea8" dark class="font-weight-black px-6" to="/credit-inquiry">
									<v-icon left>mdi-shield-account-variant-outline</v-icon>
									<span>اطلاعات بیشتر</span>
									<v-icon right small>mdi-arrow-left</v-icon>
								</v-btn>
							</div>
						</v-col>
					</v-row>
				</div>
			</v-container>
		</section>

		<!-- ========================================================================= -->
		<!-- 5. PRICING & TEST DRIVE SECTION ("قیمت ها" & "قبل از خرید تست کنید")      -->
		<!-- ========================================================================= -->
		<section id="pricing" class="landing-section pricing-hero-section">
			<!-- Background glowing orbs -->
			<div class="pricing-orb orb-p1"></div>
			<div class="pricing-orb orb-p2"></div>

			<v-container style="max-width: 1240px;" class="position-relative">
				<!-- Pricing Header -->
				<div class="text-center mb-12">
					<div class="pricing-chip mb-3">
						<v-icon small color="#ffb74d">mdi-tag-multiple-outline</v-icon>
						<span>شفاف و اقتصادی</span>
					</div>
					<h2 class="pricing-title">پلن‌ها و تعرفه قیمت‌ها</h2>
					<p class="pricing-subtitle">پلن متناسب با حجم پرونده‌های کسب‌وکار خود را انتخاب کنید</p>
				</div>

				<!-- Pricing Cards Grid -->
				<v-row class="justify-center align-stretch mb-12">
					<!-- Plan 1: Cloud Per Case -->
					<v-col cols="12" md="4">
						<div class="modern-price-card">
							<div class="price-card-header">
								<span class="plan-badge">اقتصادی</span>
								<h3 class="plan-name">پرونده‌ای سالیانه</h3>
								<div class="plan-type">سرویس‌دهی ابری (Cloud)</div>
							</div>

							<div class="price-value-box">
								<div class="price-sub">هر پرونده</div>
								<div class="price-amount">{{ cloudPrice ? cloudPrice.toString().toPriceFormat() : '...' }}</div>
								<div class="price-unit">تومان / سالانه</div>
							</div>

							<div class="plan-features-list">
								<div class="feature-line">
									<v-icon small color="#4caf50">mdi-check-circle</v-icon>
									<span>اتصال به دامنه اختصاصی شما</span>
								</div>
								<div class="feature-line">
									<v-icon small color="#4caf50">mdi-check-circle</v-icon>
									<span>اعتبار یکساله با پشتیبانی رایگان</span>
								</div>
								<div class="feature-line">
									<v-icon small color="#4caf50">mdi-check-circle</v-icon>
									<span>سیستم ارسال خودکار پیامک</span>
								</div>
								<div class="feature-line">
									<v-icon small color="#4caf50">mdi-check-circle</v-icon>
									<span>درگاه پرداخت آنلاین شاپرک</span>
								</div>
								<div class="feature-line">
									<v-icon small color="#4caf50">mdi-check-circle</v-icon>
									<span>۱۰ روز مهلت تست رایگان</span>
								</div>
							</div>

							<div class="price-card-action">
								<v-btn v-if="isAdmin" block rounded large color="#ff9800" dark :loading="loading" @click="purchase(1)">
									ثبت خرید این پلن
								</v-btn>
								<v-btn v-else block rounded large color="#522ea8" dark @click="scrollTo('test-register')">
									تست رایگان این پلن
								</v-btn>
							</div>
						</div>
					</v-col>

					<!-- Plan 2: Unlimited Cloud (Recommended) -->
					<v-col cols="12" md="4">
						<div class="modern-price-card featured-price-card">
							<div class="featured-ribbon">
								<span>پیشنهاد ویژه • محبوب‌ترین</span>
							</div>

							<div class="price-card-header">
								<span class="plan-badge badge-featured">حرفه‌ای و بدون مرز</span>
								<h3 class="plan-name">نامحدود سالیانه</h3>
								<div class="plan-type">سرویس‌دهی ابری (Cloud)</div>
							</div>

							<div class="price-value-box">
								<div class="price-sub">تعداد پرونده نامحدود</div>
								<div class="price-amount">{{ permanentPrice ? permanentPrice.toString().toPriceFormat() : '...' }}</div>
								<div class="price-unit">تومان / سالانه</div>
							</div>

							<div class="plan-features-list">
								<div class="feature-line">
									<v-icon small color="#4caf50">mdi-check-circle</v-icon>
									<span><strong>بدون محدودیت</strong> در تعداد پرونده و مشتری</span>
								</div>
								<div class="feature-line">
									<v-icon small color="#4caf50">mdi-check-circle</v-icon>
									<span>اتصال به دامنه اختصاصی فروشگاه شما</span>
								</div>
								<div class="feature-line">
									<v-icon small color="#4caf50">mdi-check-circle</v-icon>
									<span>اعتبار یکساله با پشتیبانی VIP</span>
								</div>
								<div class="feature-line">
									<v-icon small color="#4caf50">mdi-check-circle</v-icon>
									<span>ارسال خودکار پیامک‌های زمان‌بندی‌شده</span>
								</div>
								<div class="feature-line">
									<v-icon small color="#4caf50">mdi-check-circle</v-icon>
									<span>درگاه بانکی پرداخت آنلاین برای مشتریان</span>
								</div>
								<div class="feature-line">
									<v-icon small color="#4caf50">mdi-check-circle</v-icon>
									<span>۱۰ روز مهلت بررسی و تست دمو</span>
								</div>
							</div>

							<div class="price-card-action">
								<v-btn v-if="isAdmin" block rounded large color="#ff9800" dark elevation="4" :loading="loading" class="font-weight-black" @click="purchase(0)">
									خرید پلن نامحدود
								</v-btn>
								<v-btn v-else block rounded large color="#ff9800" dark elevation="4" class="font-weight-black" @click="scrollTo('test-register')">
									شروع تست رایگان پلن
								</v-btn>
							</div>
						</div>
					</v-col>

					<!-- Plan 3: Custom On-Premise -->
					<v-col cols="12" md="4">
						<div class="modern-price-card">
							<div class="price-card-header">
								<span class="plan-badge">سازمانی و دائمی</span>
								<h3 class="plan-name">دائمی سفارشی</h3>
								<div class="plan-type">نصب بر روی سرور اختصاصی</div>
							</div>

							<div class="price-value-box">
								<div class="price-sub">مالکیت دائمی نرم‌افزار</div>
								<div class="price-amount fs-h3 mt-2">تماس با فروش</div>
								<div class="price-unit">بر اساس سفارش و زیرساخت</div>
							</div>

							<div class="plan-features-list">
								<div class="feature-line">
									<v-icon small color="#4caf50">mdi-check-circle</v-icon>
									<span>نصب روی سرور یا هاست اختصاصی شما</span>
								</div>
								<div class="feature-line">
									<v-icon small color="#4caf50">mdi-check-circle</v-icon>
									<span>لایسنس دائمی و بدون انقضا</span>
								</div>
								<div class="feature-line">
									<v-icon small color="#4caf50">mdi-check-circle</v-icon>
									<span>شخصی‌سازی فرم‌ها و گزارش‌ها طبق نیاز</span>
								</div>
								<div class="feature-line">
									<v-icon small color="#4caf50">mdi-check-circle</v-icon>
									<span>پشتیبانی فنی و امنیتی اختصاصی</span>
								</div>
								<div class="feature-line">
									<v-icon small color="#4caf50">mdi-check-circle</v-icon>
									<span>آموزش کامل مدیران و پرسنل</span>
								</div>
							</div>

							<div class="price-card-action">
								<v-btn block rounded large outlined color="#ffffff" dark @click="scrollTo('consultation')">
									<v-icon left>mdi-phone</v-icon>
									مشاوره خرید اختصاصی
								</v-btn>
							</div>
						</div>
					</v-col>
				</v-row>

				<!-- ========================================================================= -->
				<!-- TEST DRIVE REGISTRATION FORM ("قبل از خرید تست کنید")                      -->
				<!-- ========================================================================= -->
				<div id="test-register" class="test-drive-card mt-16">
					<v-row class="align-center">
						<v-col cols="12" md="6" class="pl-md-8">
							<div class="test-drive-intro">
								<div class="d-flex align-center mb-3">
									<div class="test-icon-bubble ml-3">
										<v-icon color="#522ea8">mdi-shield-airplane</v-icon>
									</div>
									<h3 class="test-title">قبل از خرید به صورت رایگان تست کنید</h3>
								</div>
								<p class="test-desc">
									جهت بررسی و تست امکانات کامل نرم‌افزار، کافی است شماره موبایل خود را در فرم مقابل وارد نمایید. بلافاصله کلمه عبور ورود به سامانه برای شما پیامک خواهد شد و می‌توانید کلیه بخش‌ها را بررسی فرمایید.
								</p>

								<div class="test-steps-list">
									<div class="step-line">
										<span class="step-num">۱</span>
										<span>شماره موبایل و نام تجاری خود را ثبت کنید.</span>
									</div>
									<div class="step-line">
										<span class="step-num">۲</span>
										<span>کلمه عبور آنی پیامک شده را دریافت کنید.</span>
									</div>
									<div class="step-line">
										<span class="step-num">۳</span>
										<span>وارد پنل شوید و دمو را با اطلاعات واقعی تست کنید.</span>
									</div>
								</div>
							</div>
						</v-col>

						<v-col cols="12" md="6">
							<div class="test-form-box">
								<v-form ref="registerForm" @submit.prevent="submitRegister">
									<v-row dense>
										<v-col cols="12">
											<v-text-field ref="regName" dark filled rounded v-model="reg.name" label="نام شخصی یا عنوان فروشگاه" prepend-inner-icon="mdi-account-outline" :error-messages="errorMessages" :rules="[(value) => !!value || 'لطفاً نام را وارد کنید']" required />
										</v-col>

										<v-col cols="12">
											<v-text-field ref="regMobile" maxlength="11" dark filled rounded v-model="reg.mobile" label="شماره موبایل (مثال: ۰۹۱۲۳۴۵۶۷۸۹)" prepend-inner-icon="mdi-cellphone" :error-messages="errorMessages" :rules="[
												(value) => !!value || 'لطفاً شماره موبایل را وارد کنید',
												(value) => isPhoneNumberValid() || 'شماره موبایل نامعتبر است'
											]" required />
										</v-col>

										<v-col cols="12" class="text-center mt-2">
											<Countdown class="white--text fs-h3 mb-2" @finish="countDownFinished" ref="countdown" />

											<v-btn v-if="sendCodeVisible" :loading="regLoading" block rounded large color="#ff9800" dark type="submit" class="font-weight-black submit-test-btn">
												<v-icon left>mdi-send-lock-outline</v-icon>
												دریافت رمز عبور پیامکی
											</v-btn>

											<div v-if="showLoginToAppBtn" class="login-prompt-box pa-4 rounded-xl mt-3">
												<div class="white--text mb-2">رمز عبور با موفقیت برای شما ارسال شد.</div>
												<v-btn block rounded large color="#ffffff" class="font-weight-black brand-purple-text" @click.prevent="loginToApp">
													<v-icon left color="#522ea8">mdi-login</v-icon>
													ورود مستقیم به نرم‌افزار
												</v-btn>
											</div>

											<div v-show="regValidation" class="error-validation-box pa-3 rounded-lg mt-3">
												<v-icon small color="white" class="ml-1">mdi-alert-circle</v-icon>
												{{ regValidation }}
											</div>
										</v-col>
									</v-row>
								</v-form>
							</div>
						</v-col>
					</v-row>
				</div>
			</v-container>
		</section>

		<!-- ========================================================================= -->
		<!-- 6. CUSTOMER TESTIMONIALS SECTION ("نظر کاربران")                          -->
		<!-- ========================================================================= -->
		<section class="landing-section bg-soft-lilac">
			<v-container style="max-width: 1240px;">
				<div class="text-center mb-10">
					<div class="section-chip mb-2">
						<v-icon small color="#ff9800">mdi-heart-outline</v-icon>
						<span>اعتماد مشتریان</span>
					</div>
					<h2 class="section-main-heading">نظرات کاربران درباره جت اقساط</h2>
					<p class="section-sub-heading">تجربه صاحبان کسب‌وکار و فروشگاه‌ها پس از استقرار جت اقساط</p>
				</div>

				<v-card class="testimonial-carousel-card mx-auto rounded-2xl" elevation="3">
					<v-carousel dark height="280" cycle interval="6000" hide-delimiter-background show-arrows-on-hover>
						<v-carousel-item>
							<div class="testimonial-slide">
								<div class="testimonial-avatar">
									<v-icon size="48" color="#522ea8">mdi-store</v-icon>
								</div>
								<div class="rating-stars my-2 d-flex">
									<v-icon small color="#ffb74d" v-for="i in 5" :key="i">mdi-star</v-icon>
								</div>
								<h3 class="customer-name">فروشگاه موبایل زندی</h3>
								<p class="customer-quote">
									«مدت‌ها بود به دنبال یک نرم‌افزار تحت وب جامع برای ساماندهی اقساط مشتریان بودیم. پیامک‌های خودکار یادآوری و درگاه پرداخت آنلاین جت اقساط باعث شد وصول مطالبات ما بیش از ۴۰ درصد سریع‌تر شود.»
								</p>
							</div>
						</v-carousel-item>

						<v-carousel-item>
							<div class="testimonial-slide">
								<div class="testimonial-avatar">
									<v-icon size="48" color="#522ea8">mdi-diamond-stone</v-icon>
								</div>
								<div class="rating-stars my-2 d-flex">
									<v-icon small color="#ffb74d" v-for="i in 5" :key="i">mdi-star</v-icon>
								</div>
								<h3 class="customer-name">گالری جواهر آویژه</h3>
								<p class="customer-quote">
									«محاسبه خودکار دیرکرد، زودکرد و به ویژه فرمول تسویه آنی که با یک کلیک انجام می‌شود از بزرگترین نقاط قوت جت اقساط است. خطاهای دستی ما کاملاً به صفر رسید.»
								</p>
							</div>
						</v-carousel-item>

						<v-carousel-item>
							<div class="testimonial-slide">
								<div class="testimonial-avatar">
									<v-icon size="48" color="#522ea8">mdi-television-classic</v-icon>
								</div>
								<div class="rating-stars my-2 d-flex">
									<v-icon small color="#ffb74d" v-for="i in 5" :key="i">mdi-star</v-icon>
								</div>
								<h3 class="customer-name">لوازم خانگی اولیائی</h3>
								<p class="customer-quote">
									«سادگی کار با نرم‌افزار و پاسخگویی دلسوزانه تیم پشتیبانی کم‌نظیر است. مشتریان ما هم از اینکه می‌توانند اقساط را آنلاین پرداخت کنند بسیار راضی هستند.»
								</p>
							</div>
						</v-carousel-item>
					</v-carousel>
				</v-card>
			</v-container>
		</section>
	</div>
</template>

<script>
import { get } from 'vuex-pathify'
import api from '@/util/app-api'
import toast from '@/util/toast'
import router from '@/router'
import { getCookie } from '@/util/helpers'

export default {
	name: 'HomeView',

	components: {
		Countdown: () => import('@/components/Countdown'),
	},

	data: () => ({
		errorMessages: '',
		reg: { name: '', mobile: '' },
		loading: false,
		regLoading: false,
		regValidation: '',
		sendCodeVisible: true,
		showLoginToAppBtn: false,

		// Mouse move microinteraction state
		tiltX: 0,
		tiltY: 0,
		badgeX1: 0,
		badgeY1: 0,
		badgeX2: 0,
		badgeY2: 0,
		badgeX3: 0,
		badgeY3: 0,
		spotlightX: 50,
		spotlightY: 50
	}),

	computed: {
		...get('auth', [
			'isAdmin', 'getCurrentUser'
		]),
		...get('siteConfig', [
			'permanentPrice', 'cloudPrice'
		]),
		whatsappUrl() {
			return 'https://wa.me/989361995434?text=' + encodeURIComponent('سلام، برای اعتبارسنجی و استعلام آنلاین در سامانه جت اقساط درخواست مشاوره داشتم.')
		}
	},

	mounted() {
		// Check if URL contains hash on load and smooth scroll
		if (this.$route.hash) {
			const sectionId = this.$route.hash.replace('#', '')
			this.$nextTick(() => {
				setTimeout(() => {
					this.scrollTo(sectionId)
				}, 350)
			})
		}
	},

	watch: {
		'$route.hash'(newHash) {
			if (newHash) {
				const sectionId = newHash.replace('#', '')
				this.$nextTick(() => {
					setTimeout(() => {
						this.scrollTo(sectionId)
					}, 200)
				})
			}
		}
	},

	methods: {
		// Hero banner mouse move interaction
		onHeroMouseMove(e) {
			const banner = this.$refs.heroBanner
			if (!banner) return
			const rect = banner.getBoundingClientRect()
			const x = (e.clientX - rect.left) / rect.width - 0.5 // -0.5 to 0.5
			const y = (e.clientY - rect.top) / rect.height - 0.5 // -0.5 to 0.5

			// Calculate 3D card tilt angles
			this.tiltX = y * -12
			this.tiltY = x * 14

			// Parallax offsets for floating badges
			this.badgeX1 = x * -22
			this.badgeY1 = y * -16

			this.badgeX2 = x * 26
			this.badgeY2 = y * 18

			this.badgeX3 = x * -18
			this.badgeY3 = y * 22

			// Cursor spotlight center in percentage
			this.spotlightX = Math.round(((e.clientX - rect.left) / rect.width) * 100)
			this.spotlightY = Math.round(((e.clientY - rect.top) / rect.height) * 100)
		},

		onHeroMouseLeave() {
			this.tiltX = 0
			this.tiltY = 0
			this.badgeX1 = 0
			this.badgeY1 = 0
			this.badgeX2 = 0
			this.badgeY2 = 0
			this.badgeX3 = 0
			this.badgeY3 = 0
			this.spotlightX = 50
			this.spotlightY = 50
		},

		// Smooth scroll to target element
		scrollTo(sectionId) {
			const el = document.getElementById(sectionId)
			if (el) {
				const navOffset = 95
				const elementPosition = el.getBoundingClientRect().top
				const offsetPosition = elementPosition + window.pageYOffset - navOffset
				window.scrollTo({
					top: offsetPosition,
					behavior: 'smooth'
				})

				if (window.history && window.history.pushState) {
					window.history.pushState(null, null, `#${sectionId}`)
				}
			}
		},

		purchase(productType) {
			this.loading = true
			let data = {
				productType: productType,
				paymentMethod: 0
			}
			api.post('admin/purchase/create', data)
				.then(response => {
					window.location.replace(response)
				})
				.catch(err => {
					this.loading = false
					toast.error(err.message)
				})
		},

		submitRegister() {
			if (!this.$refs.registerForm.validate()) { return }
			this.regValidation = ''
			this.regLoading = true

			const formData = new FormData()
			formData.append('name', this.reg.name)
			formData.append('mobile', this.fixPhoneNumber(this.reg.mobile))
			formData.append('sendSms', true)
			const affCode = getCookie('aff')
			if (affCode) {
				formData.append('affCode', affCode)
			}
			api.post('account/register', formData, { authHeader: false, contentType: 'multipart/form-data' })
				.then(() => {
					if (this.$refs.countdown && this.$refs.countdown.start) {
						this.$refs.countdown.start()
					}
					this.sendCodeVisible = false
					this.showLoginToAppBtn = true
				})
				.catch(err => {
					this.regValidation = err.message
				})
				.finally(() => {
					this.regLoading = false
				})
		},

		countDownFinished() {
			this.sendCodeVisible = true
		},

		isPhoneNumberValid() {
			return this.reg.mobile && (/^((?!(0))[0-9]{10})$/g.test(this.reg.mobile) || /^0[0-9]\d{9}$/g.test(this.reg.mobile))
		},

		loginToApp() {
			router.push('/login/0/?from=home').catch(() => { })
		},

		fixPhoneNumber(number) {
			if (number?.length === 10 && !number.startsWith('0'))
				number = '0' + number
			return number
		}
	}
}
</script>

<style lang="scss">
/* Base Variables and Root Styles */
.landing-page-root {
	font-family: "iranyekanweb", "Emkan-Regular", "Yekan", tahoma, sans-serif;
	color: #2b233a;
	overflow-x: hidden;
}

.landing-section {
	padding: 70px 0;
	position: relative;
}

.bg-soft-lilac {
	background: #f8f6fc;
}

/* ========================================================================= */
/* 1. HERO BANNER STYLES                                                     */
/* ========================================================================= */
.hero-banner-section {
	width: 100%;
	position: relative;
	overflow: hidden;
	background: linear-gradient(135deg, #ffffff 0%, #f7f4fc 35%, #eee7f9 70%, #ffffff 100%);
	border-bottom: 1px solid rgba(82, 46, 168, 0.08);
	min-height: 600px;
	display: flex;
	align-items: center;
}

.hero-spotlight-glow {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	pointer-events: none;
	transition: background 0.15s ease-out;
}

.hero-orb {
	position: absolute;
	border-radius: 50%;
	filter: blur(80px);
	pointer-events: none;
	opacity: 0.65;
}

.hero-orb-1 {
	width: 450px;
	height: 450px;
	background: rgba(110, 65, 220, 0.14);
	top: -100px;
	right: -100px;
}

.hero-orb-2 {
	width: 380px;
	height: 380px;
	background: rgba(82, 46, 168, 0.1);
	bottom: -50px;
	left: 10%;
}

.hero-grid-pattern {
	position: absolute;
	inset: 0;
	background-image: radial-gradient(rgba(82, 46, 168, 0.08) 1.5px, transparent 1.5px);
	background-size: 24px 24px;
	opacity: 0.5;
	pointer-events: none;
}

.hero-container {
	position: relative;
	z-index: 2;
	max-width: 1240px;
}

/* Hero Left (Text content in RTL) */
.hero-chip {
	display: inline-flex;
	align-items: center;
	padding: 6px 16px;
	border-radius: 50px;
	background: rgba(82, 46, 168, 0.08);
	border: 1px solid rgba(82, 46, 168, 0.18);
	color: #522ea8;
	font-size: 0.84rem;
	font-weight: 700;
}

.hero-pulse-dot {
	width: 8px;
	height: 8px;
	border-radius: 50%;
	background-color: #ff9800;
	margin-left: 6px;
	box-shadow: 0 0 0 0 rgba(255, 152, 0, 0.7);
	animation: pulseDot 2s infinite;
}

@keyframes pulseDot {
	0% {
		transform: scale(0.95);
		box-shadow: 0 0 0 0 rgba(255, 152, 0, 0.7);
	}

	70% {
		transform: scale(1);
		box-shadow: 0 0 0 6px rgba(255, 152, 0, 0);
	}

	100% {
		transform: scale(0.95);
		box-shadow: 0 0 0 0 rgba(255, 152, 0, 0);
	}
}

.hero-title {
	font-size: 2.35rem;
	font-weight: 900;
	line-height: 1.4;
	color: #241442;
	font-family: "Emkan-Bold", "iranyekanweb", sans-serif;
}

.hero-title-highlight {
	color: #522ea8;
	position: relative;
	display: inline-block;

	&::after {
		content: "";
		position: absolute;
		bottom: 4px;
		left: 0;
		right: 0;
		height: 8px;
		background: rgba(82, 46, 168, 0.14);
		border-radius: 4px;
		z-index: -1;
	}
}

.hero-subtitle {
	font-size: 1.05rem;
	line-height: 2.1;
	color: #554a6b;
	text-align: justify;
}

.hero-actions {
	display: flex;
	align-items: center;
	flex-wrap: wrap;

	.hero-btn-primary {
		background: linear-gradient(135deg, #522ea8 0%, #6e41dc 100%) !important;
		color: #ffffff !important;
		font-weight: 800;
		font-size: 1rem;
		padding: 0 28px !important;
		height: 52px !important;
		box-shadow: 0 8px 24px rgba(82, 46, 168, 0.35) !important;
		transition: all 0.3s ease !important;

		&:hover {
			transform: translateY(-3px);
			box-shadow: 0 12px 30px rgba(82, 46, 168, 0.45) !important;
			background: linear-gradient(135deg, #44238e 0%, #5d34c0 100%) !important;
		}
	}

	.hero-btn-secondary {
		border: 2px solid #522ea8 !important;
		color: #522ea8 !important;
		font-weight: 800;
		font-size: 1rem;
		padding: 0 28px !important;
		height: 52px !important;
		background: rgba(255, 255, 255, 0.7) !important;
		backdrop-filter: blur(8px);
		transition: all 0.3s ease !important;

		&:hover {
			background: #522ea8 !important;
			color: #ffffff !important;
			transform: translateY(-3px);
			box-shadow: 0 8px 20px rgba(82, 46, 168, 0.25) !important;
		}
	}

	.hero-btn-whatsapp {
		background: linear-gradient(135deg, #25D366 0%, #1ea952 100%) !important;
		color: #ffffff !important;
		font-weight: 800;
		font-size: 1rem;
		padding: 0 26px !important;
		height: 52px !important;
		box-shadow: 0 8px 24px rgba(37, 211, 102, 0.35) !important;
		transition: all 0.3s ease !important;

		&:hover {
			transform: translateY(-3px);
			box-shadow: 0 12px 30px rgba(37, 211, 102, 0.5) !important;
		}
	}
}

.hero-trust-row {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 16px;
	padding-top: 12px;
	border-top: 1px dashed rgba(82, 46, 168, 0.16);

	.trust-item {
		display: flex;
		align-items: center;
		gap: 6px;
		font-size: 0.85rem;
		font-weight: 600;
		color: #594d73;
	}
}

/* Hero Right (3D Visual Showcase & Parallax) */
.hero-visual-wrapper {
	position: relative;
	display: flex;
	justify-content: center;
	align-items: center;
	padding: 24px 12px;
}

.hero-3d-card {
	width: 100%;
	max-width: 520px;
	background: #ffffff;
	border-radius: 20px;
	overflow: hidden;
	box-shadow: 0 24px 60px -15px rgba(82, 46, 168, 0.22), 0 0 1px 1px rgba(82, 46, 168, 0.1);
	border: 1px solid rgba(82, 46, 168, 0.15);
	transition: transform 0.15s ease-out, box-shadow 0.2s ease-out;
	will-change: transform;
}

.mockup-window-header {
	background: #f7f5fa;
	padding: 10px 16px;
	display: flex;
	align-items: center;
	justify-content: space-between;
	border-bottom: 1px solid rgba(0, 0, 0, 0.05);

	.mockup-dots {
		display: flex;
		gap: 6px;

		.dot {
			width: 10px;
			height: 10px;
			border-radius: 50%;
		}

		.dot-red {
			background: #ff5f56;
		}

		.dot-yellow {
			background: #ffbd2e;
		}

		.dot-green {
			background: #27c93f;
		}
	}

	.mockup-title {
		font-size: 0.75rem;
		color: #6d6287;
		font-weight: 600;
		display: flex;
		align-items: center;
	}
}

.mockup-screen-body {
	position: relative;
	overflow: hidden;

	.mockup-main-image {
		width: 100%;
		display: block;
		object-fit: cover;
	}

	.mockup-glass-sheen {
		position: absolute;
		inset: 0;
		pointer-events: none;
	}
}

/* Floating Parallax Badges */
.parallax-badge {
	position: absolute;
	display: flex;
	align-items: center;
	padding: 10px 14px;
	background: rgba(255, 255, 255, 0.94);
	backdrop-filter: blur(14px);
	-webkit-backdrop-filter: blur(14px);
	border-radius: 16px;
	border: 1px solid rgba(82, 46, 168, 0.15);
	box-shadow: 0 12px 30px rgba(82, 46, 168, 0.18), 0 2px 8px rgba(0, 0, 0, 0.04);
	z-index: 5;
	transition: transform 0.2s ease-out;
	user-select: none;

	.badge-icon-box {
		width: 36px;
		height: 36px;
		border-radius: 10px;
		display: flex;
		align-items: center;
		justify-content: center;
		margin-left: 10px;

		&.bg-amber {
			background: rgba(255, 152, 0, 0.14);
		}

		&.bg-purple {
			background: rgba(82, 46, 168, 0.12);
		}

		&.bg-emerald {
			background: rgba(46, 125, 50, 0.12);
		}
	}

	.badge-text-box {
		display: flex;
		flex-direction: column;

		.badge-primary-text {
			font-size: 0.8rem;
			font-weight: 800;
			color: #271a3e;
			line-height: 1.2;
		}

		.badge-secondary-text {
			font-size: 0.68rem;
			color: #71658b;
			margin-top: 1px;
		}
	}

	&.badge-top {
		top: 10px;
		left: -15px;
	}

	&.badge-bottom-right {
		bottom: 25px;
		right: -15px;
	}

	&.badge-bottom-left {
		bottom: -15px;
		left: 15px;
	}
}

/* ========================================================================= */
/* 2. SECTION: "جت اقساط در ۲ دقیقه"                                         */
/* ========================================================================= */
.jet-2min-section {
	background: #ffffff;
	border-bottom: 1px solid rgba(82, 46, 168, 0.08);
}

.section-badge-header {
	.section-chip {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		padding: 5px 14px;
		border-radius: 50px;
		background: rgba(255, 152, 0, 0.1);
		color: #e65100;
		font-size: 0.82rem;
		font-weight: 700;
	}

	.section-main-heading {
		font-size: 2.1rem;
		font-weight: 900;
		color: #241442;
		font-family: "Emkan-Bold", "iranyekanweb", sans-serif;
		margin: 8px 0;
	}

	.section-sub-heading {
		font-size: 1rem;
		color: #6c5f87;
	}
}

.video-card-container {
	background: linear-gradient(135deg, #f7f5fb 0%, #ede6f8 100%);
	padding: 12px;
	border-radius: 26px;
	border: 1px solid rgba(82, 46, 168, 0.18);
	box-shadow: 0 16px 45px -10px rgba(82, 46, 168, 0.2);

	.video-card-inner {
		border-radius: 20px;
		overflow: hidden;
		background: #000;
	}

	.video-frame-wrapper {
		position: relative;
		width: 100%;
		padding-bottom: 56.25%;
		/* 16:9 Aspect Ratio */
		height: 0;

		iframe {
			position: absolute;
			top: 0;
			left: 0;
			width: 100%;
			height: 100%;
			border: 0;
		}
	}

	.video-footer-strip {
		background: #ffffff;
		padding: 12px 18px;
		display: flex;
		align-items: center;
		justify-content: space-between;

		.video-pulse-play {
			width: 26px;
			height: 26px;
			border-radius: 50%;
			background: #522ea8;
			display: flex;
			align-items: center;
			justify-content: center;
		}

		.video-title-text {
			font-size: 0.88rem;
			font-weight: 700;
			color: #352553;
		}

		.aparat-link-btn {
			display: inline-flex;
			align-items: center;
			font-size: 0.8rem;
			font-weight: 700;
			color: #e65100;
			text-decoration: none;
			padding: 4px 10px;
			border-radius: 8px;
			background: rgba(255, 152, 0, 0.1);
			transition: all 0.2s ease;

			&:hover {
				background: rgba(255, 152, 0, 0.2);
			}
		}
	}
}

.jet-benefits-box {
	.jet-benefits-title {
		font-size: 1.35rem;
		font-weight: 900;
		color: #241442;
	}

	.benefit-card {
		display: flex;
		align-items: flex-start;
		padding: 12px 14px;
		border-radius: 16px;
		background: #fbf9fd;
		border: 1px solid rgba(82, 46, 168, 0.08);
		transition: all 0.3s ease;

		&:hover {
			transform: translateY(-2px);
			background: #ffffff;
			box-shadow: 0 8px 24px rgba(82, 46, 168, 0.1);
			border-color: rgba(82, 46, 168, 0.2);
		}

		.benefit-icon {
			width: 44px;
			height: 44px;
			border-radius: 12px;
			display: flex;
			align-items: center;
			justify-content: center;
			margin-left: 14px;
			flex-shrink: 0;

			&.bg-light-purple {
				background: rgba(82, 46, 168, 0.1);
			}

			&.bg-light-amber {
				background: rgba(255, 152, 0, 0.12);
			}

			&.bg-light-emerald {
				background: rgba(46, 125, 50, 0.1);
			}

			&.bg-light-blue {
				background: rgba(2, 136, 209, 0.1);
			}
		}

		.benefit-content {
			min-width: 0;

			h4 {
				font-size: 0.95rem;
				font-weight: 800;
				color: #291a42;
				margin: 0;
			}

			p {
				font-size: 0.84rem;
				line-height: 1.65;
				color: #64587c;
				margin: 0;
			}

			.benefit-action-btn {
				height: 26px !important;
				font-size: 0.75rem !important;
				letter-spacing: 0 !important;
				padding: 0 8px !important;
				border-width: 1px;
				flex-shrink: 0;
			}
		}
	}
}

/* ========================================================================= */
/* 4. PROFESSIONAL FEATURES GRID                                             */
/* ========================================================================= */
.pro-feature-card {
	background: #ffffff;
	border-radius: 20px;
	padding: 28px 22px;
	border: 1px solid rgba(82, 46, 168, 0.08);
	box-shadow: 0 8px 20px rgba(82, 46, 168, 0.05);
	transition: all 0.3s ease;
	height: 100%;

	&:hover {
		transform: translateY(-6px);
		box-shadow: 0 16px 35px rgba(82, 46, 168, 0.14);
		border-color: rgba(82, 46, 168, 0.2);

		.pro-icon-bubble {
			background: #522ea8;
			transform: rotate(6deg);

			.v-icon {
				color: #ffffff !important;
			}
		}
	}

	.pro-icon-bubble {
		width: 58px;
		height: 58px;
		border-radius: 18px;
		background: rgba(82, 46, 168, 0.08);
		display: flex;
		align-items: center;
		justify-content: center;
		margin-bottom: 16px;
		transition: all 0.3s ease;
	}

	.pro-title {
		font-size: 1.15rem;
		font-weight: 800;
		color: #271940;
		margin-bottom: 8px;
	}

	.pro-desc {
		font-size: 0.88rem;
		color: #695e80;
		line-height: 1.8;
		margin: 0;
	}
}

/* ========================================================================= */
/* 5. PRICING SECTION STYLES                                                 */
/* ========================================================================= */
.pricing-hero-section {
	background: linear-gradient(145deg, #1d0f36 0%, #2f175a 50%, #170b2d 100%);
	position: relative;
	overflow: hidden;
	padding: 85px 0;
	color: #ffffff;
}

.pricing-orb {
	position: absolute;
	border-radius: 50%;
	filter: blur(90px);
	pointer-events: none;
}

.orb-p1 {
	width: 450px;
	height: 450px;
	background: rgba(110, 65, 220, 0.2);
	top: -80px;
	left: 10%;
}

.orb-p2 {
	width: 400px;
	height: 400px;
	background: rgba(255, 152, 0, 0.12);
	bottom: 10%;
	right: 5%;
}

.pricing-chip {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	padding: 5px 16px;
	border-radius: 50px;
	background: rgba(255, 183, 77, 0.15);
	color: #ffb74d;
	font-size: 0.85rem;
	font-weight: 700;
}

.pricing-title {
	font-size: 2.2rem;
	font-weight: 900;
	color: #ffffff;
	font-family: "Emkan-Bold", "iranyekanweb", sans-serif;
}

.pricing-subtitle {
	font-size: 1rem;
	color: rgba(255, 255, 255, 0.7);
}

.modern-price-card {
	background: rgba(255, 255, 255, 0.06);
	backdrop-filter: blur(14px);
	-webkit-backdrop-filter: blur(14px);
	border: 1px solid rgba(255, 255, 255, 0.12);
	border-radius: 26px;
	padding: 32px 24px;
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	position: relative;
	transition: all 0.35s ease;
	height: 100%;

	&:hover {
		transform: translateY(-8px);
		border-color: rgba(255, 255, 255, 0.25);
		background: rgba(255, 255, 255, 0.09);
		box-shadow: 0 20px 45px rgba(0, 0, 0, 0.3);
	}

	&.featured-price-card {
		background: rgba(255, 255, 255, 0.1);
		border: 2px solid #ff9800;
		box-shadow: 0 20px 50px rgba(0, 0, 0, 0.35);

		.featured-ribbon {
			position: absolute;
			top: -14px;
			right: 24px;
			background: linear-gradient(135deg, #ff9800 0%, #f57c00 100%);
			color: #ffffff;
			font-size: 0.75rem;
			font-weight: 900;
			padding: 4px 14px;
			border-radius: 20px;
			box-shadow: 0 4px 12px rgba(245, 124, 0, 0.4);
		}
	}

	.plan-badge {
		display: inline-block;
		padding: 4px 12px;
		border-radius: 20px;
		background: rgba(255, 255, 255, 0.1);
		font-size: 0.78rem;
		color: rgba(255, 255, 255, 0.85);
		margin-bottom: 8px;

		&.badge-featured {
			background: rgba(255, 152, 0, 0.2);
			color: #ffb74d;
		}
	}

	.plan-name {
		font-size: 1.45rem;
		font-weight: 900;
		color: #ffffff;
		margin-bottom: 4px;
	}

	.plan-type {
		font-size: 0.84rem;
		color: rgba(255, 255, 255, 0.6);
	}

	.price-value-box {
		margin: 24px 0;
		padding: 16px;
		border-radius: 16px;
		background: rgba(0, 0, 0, 0.2);
		text-align: center;

		.price-sub {
			font-size: 0.82rem;
			color: rgba(255, 255, 255, 0.65);
		}

		.price-amount {
			font-size: 2.2rem;
			font-weight: 900;
			color: #ffffff;
			font-family: "yekan", sans-serif;
		}

		.price-unit {
			font-size: 0.82rem;
			color: rgba(255, 255, 255, 0.65);
		}
	}

	.plan-features-list {
		display: flex;
		flex-direction: column;
		gap: 12px;
		margin-bottom: 28px;

		.feature-line {
			display: flex;
			align-items: center;
			gap: 10px;
			font-size: 0.88rem;
			color: rgba(255, 255, 255, 0.88);
		}
	}
}

/* Test Drive Box */
.test-drive-card {
	background: rgba(255, 255, 255, 0.05);
	backdrop-filter: blur(14px);
	-webkit-backdrop-filter: blur(14px);
	border-radius: 28px;
	border: 1px solid rgba(255, 255, 255, 0.14);
	padding: 36px 30px;
	box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);

	.test-icon-bubble {
		width: 48px;
		height: 48px;
		border-radius: 14px;
		background: #ffffff;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.test-title {
		font-size: 1.35rem;
		font-weight: 900;
		color: #ffffff;
	}

	.test-desc {
		font-size: 0.95rem;
		color: rgba(255, 255, 255, 0.75);
		line-height: 1.9;
	}

	.test-steps-list {
		display: flex;
		flex-direction: column;
		gap: 12px;
		margin-top: 18px;

		.step-line {
			display: flex;
			align-items: center;
			gap: 12px;
			font-size: 0.9rem;
			color: rgba(255, 255, 255, 0.9);

			.step-num {
				width: 24px;
				height: 24px;
				border-radius: 50%;
				background: #ff9800;
				color: #ffffff;
				display: flex;
				align-items: center;
				justify-content: center;
				font-weight: 800;
				font-size: 0.8rem;
				flex-shrink: 0;
			}
		}
	}

	.test-form-box {
		.v-input__slot {
			background: rgba(14, 7, 28, 0.6) !important;
			border: 1px solid rgba(255, 255, 255, 0.12) !important;
			transition: border-color 0.25s ease;

			&:hover {
				border-color: rgba(110, 65, 220, 0.5) !important;
			}
		}

		.submit-test-btn {
			background: linear-gradient(135deg, #ff9800 0%, #f57c00 100%) !important;
			box-shadow: 0 6px 20px rgba(245, 124, 0, 0.35) !important;
		}

		.login-prompt-box {
			background: rgba(82, 46, 168, 0.4);
			border: 1px solid rgba(255, 255, 255, 0.2);

			.brand-purple-text {
				color: #522ea8 !important;
			}
		}

		.error-validation-box {
			background: rgba(233, 30, 99, 0.8);
			color: #ffffff;
			font-size: 0.88rem;
		}
	}
}

/* ========================================================================= */
/* 6. TESTIMONIALS SECTION                                                   */
/* ========================================================================= */
.testimonial-carousel-card {
	background: #ffffff !important;
	border: 1px solid rgba(82, 46, 168, 0.1) !important;
	box-shadow: 0 12px 35px rgba(82, 46, 168, 0.08) !important;
	max-width: 820px;
	overflow: hidden;

	.testimonial-slide {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		text-align: center;
		height: 100%;
		padding: 24px 32px;

		.testimonial-avatar {
			width: 72px;
			height: 72px;
			border-radius: 50%;
			background: rgba(82, 46, 168, 0.08);
			display: flex;
			align-items: center;
			justify-content: center;
		}

		.customer-name {
			font-size: 1.2rem;
			font-weight: 800;
			color: #241442;
			margin-bottom: 6px;
		}

		.customer-quote {
			font-size: 0.95rem;
			line-height: 1.9;
			color: #5d5276;
			max-width: 640px;
			margin: 0;
		}
	}
}

/* Responsive Tweaks */
@media (max-width: 959px) {
	.hero-title {
		font-size: 1.85rem;
	}

	.hero-banner-section {
		padding: 30px 0;
	}

	.parallax-badge {
		display: none;
		/* Hide floating badges on small mobile screens to keep layout tidy */
	}

	.hero-actions {
		justify-content: center;

		.hero-btn-primary,
		.hero-btn-secondary,
		.hero-btn-whatsapp {
			width: 100%;
		}
	}

	.hero-subtitle {
		text-align: right;
	}
}

/* ========================================================================= */
/* INQUIRY SHOWCASE SECTION STYLES                                           */
/* ========================================================================= */
.inquiry-feature-section {
	.inquiry-showcase-card {
		background: #ffffff;
		border: 1px solid rgba(82, 46, 168, 0.12);
		box-shadow: 0 16px 45px rgba(82, 46, 168, 0.08);
		transition: all 0.3s ease;

		&:hover {
			box-shadow: 0 20px 50px rgba(82, 46, 168, 0.14);
			border-color: rgba(82, 46, 168, 0.22);
		}
	}

	.inquiry-mockup-wrapper {
		position: relative;
		display: flex;
		justify-content: center;
		align-items: center;

		.inquiry-feature-img {
			width: 100%;
			max-width: 460px;
			height: auto;
			object-fit: cover;
			border: 1px solid rgba(82, 46, 168, 0.15);
			transition: transform 0.3s ease, box-shadow 0.3s ease;

			&:hover {
				transform: translateY(-4px);
				box-shadow: 0 16px 32px rgba(82, 46, 168, 0.18) !important;
			}
		}
	}

	.whatsapp-outline-btn {
		border: 2px solid #25D366 !important;
		color: #25D366 !important;
		transition: all 0.25s ease;

		&:hover {
			background: #25D366 !important;
			color: #ffffff !important;

			.v-icon {
				color: #ffffff !important;
			}
		}
	}
}
</style>